import React, { useEffect, useState } from "react";
import { Button, Input, Select, Table, Spin, notification, Modal, Dropdown } from "antd";
import {
  ArrowLeftOutlined,
  CloseOutlined,
  SearchOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { LuFilter } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";
import {
  useDeleteCampaignMutation,
  useDeleteFormMutation,
  useGetOrganizationByIdQuery,
  useUpdateFormMutation,
  useUpdateFormStatusMutation,
  useUpdateOrganizationMutation,
  useActivateFormMutation,
  useDeactivateFormMutation,
  useUpdateDatesActivateMutation,
} from "../../../store/api/apiSlice";
import moment from "moment";
// import { getStatusColor } from '../../../utils/helpers';
import { basePath, getStatusColor, getStatusDisplayText } from "../../../utils";
import { withDerivedCampaignStatuses } from "../../../utils/campaigns";
import CreateFormModal from "../../../components/Modals/CreateFormModal";
import { LayoutGrid, List } from "lucide-react";
import CreateCampaignModal from "../../../components/Modals/CreateCampaignModal";
import ActivateDateWarningModal from "../../../components/Modals/ActivateDateWarningModal";

const OrganizationDetails = () => {  const { id: organizationId } = useParams();
  const [activeTab, setActiveTab] = useState("campaigns");

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const navigate = useNavigate();
  // Local state
  const [searchText, setSearchText] = useState("");
  const [sort, setSortBy] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingForm, setEditingForm] = useState(false);
  const [createFormModalVisible, setCreateFormModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Date modal state
  const [showActivateDateModal, setShowActivateDateModal] = useState(false);
  const [selectedFormForActivation, setSelectedFormForActivation] =
    useState(null);
  const [isStarting, setIsStarting] = useState(false);

  const [deleteCampaign] = useDeleteCampaignMutation();
  // API hooks
  const {
    data: organizationData,
    isLoading,
    refetch: refetchOrganization,
  } = useGetOrganizationByIdQuery(organizationId);
  const [updateFormStatus] = useUpdateFormStatusMutation();
  const [deleteForm] = useDeleteFormMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const [activateForm] = useActivateFormMutation();
  const [deactivateForm] = useDeactivateFormMutation();
  const [updateDatesActivate] = useUpdateDatesActivateMutation();
  const [updateForm] = useUpdateFormMutation();

  const handleSuccess = () => {
    refetchOrganization(); // use the destructured refetch function
  };

  // Utility functions for date validation
  const toUnixSecondsString = (dateStr) => {
    if (!dateStr) return null;
    return String(Math.floor(new Date(dateStr).getTime() / 1000));
  };

  const isToday = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  const isTodayOrFuture = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    today.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate >= today;
  };
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Auto-switch to grid view on mobile
      if (mobile) {
        setViewType("grid");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this form?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteForm(record._id).unwrap();
          notification.success({
            message: "Success",
            description: "Form deleted successfully",
          });
          refetchOrganization(); // Refresh data after delete
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.data?.message || "Failed to delete form",
          });
        }
      },
    });
  };
  // Filter and sort data
  const filterData = (data = []) => {
    return data
      .filter((item) => {
        const matchesSearch =
          !searchText ||
          item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = !statusFilter || item.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        if (sort === "newest") {
          return new Date(b.date_created) - new Date(a.date_created);
        }
        return new Date(a.date_created) - new Date(b.date_created);
      });
  };
  const handleEditCampaign = (record) => {
    // Ensure organization data is included when editing from organization details
    const campaignWithOrg = {
      ...record,
      organization: {
        _id: organizationId,
        name: organizationData?.data?.name || "Current Organisation ",
      },
    };
    setEditingCampaign(campaignWithOrg);
    setIsModalVisible(true);
  };

  const StatCard = ({ value, label }) => (
    <div className="bg-secondary rounded-xl p-6">
      <div className="flex flex-col">
        <div className="text-3xl font-bold text-gray-900">{value}</div>
        <div className="text-gray-500 text-sm">{label}</div>
      </div>
    </div>
  );

  const handleFormDelete = (formId) => {
    Modal.confirm({
      title: "Delete Form",
      content: "Are you sure you want to delete this form?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteForm(formId).unwrap();
          notification.success({
            message: "Success",
            description: "Form deleted successfully",
          });
          refetchOrganization();
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete form",
          });
        }
      },
    });
  };
  const handleStatusChange = async (form, newStatus) => {
    try {
      if (newStatus === "active") {
        // Check if form has start and end dates
        if (!form.start_date || !form.end_date) {
          notification.error({
            message: "Missing Dates",
            description: "Form must have start and end dates to be activated",
          });
          return;
        }

        // Convert Unix timestamps to dates for comparison
        const startDate = new Date(parseInt(form.start_date) * 1000);
        const endDate = new Date(parseInt(form.end_date) * 1000);
        const today = new Date();

        // Check if current date is within the form's date range
        const isWithinRange = today >= startDate && today <= endDate;

        if (!isWithinRange) {
          // Out of range: open modal to adjust dates
          setSelectedFormForActivation(form);
          setShowActivateDateModal(true);
          return;
        }

        // In range: activate immediately
        setIsStarting(true);
        await activateForm(form._id).unwrap();
        notification.success({
          message: "Success",
          description: "Form activated successfully",
        });
      } else if (newStatus === "not_active") {
        await deactivateForm(form._id).unwrap();
      } else {
        // For other statuses, use the old updateFormStatus
        await updateFormStatus({
          id: form._id,
          status: newStatus,
        }).unwrap();
        notification.success({
          message: "Success",
          description: `Form status updated to ${newStatus}`,
        });
      }
      refetchOrganization();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to update status",
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Handler for date modal confirmation
  const handleConfirmActivationWithDates = async ({ startDate, endDate }) => {
    if (!selectedFormForActivation) return;

    try {
      setIsStarting(true);

      // Validate dates before making API call
      if (!startDate || !endDate) {
        notification.error({
          message: "Validation Error",
          description: "Please select both start and end dates",
        });
        return;
      }

      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      if (startDateObj >= endDateObj) {
        notification.error({
          message: "Validation Error",
          description: "End date must be after start date",
        });
        return;
      }

      await updateDatesActivate({
        id: selectedFormForActivation._id,
        startDate: toUnixSecondsString(startDate),
        endDate: toUnixSecondsString(endDate),
      }).unwrap();

      notification.success({
        message: "Success",
        description: "Form dates updated and activated successfully",
      });

      // Refresh the organization data
      refetchOrganization();
    } catch (error) {
      console.error("Error updating form dates and activating:", error);
      if (error?.status === 400) {
        notification.error({
          message: "Validation Error",
          description:
            error?.data?.message || "Invalid date range or form data",
        });
      } else if (error?.status === 404) {
        notification.error({
          message: "Form Not Found",
          description: "The form you're trying to activate no longer exists",
        });
      } else if (error?.status === 500) {
        notification.error({
          message: "Server Error",
          description:
            "An error occurred on the server. Please try again later.",
        });
      } else if (error?.status === 401 || error?.status === 403) {
        notification.error({
          message: "Permission Denied",
          description: "You don't have permission to activate this form",
        });
      } else {
        notification.error({
          message: "Activation Failed",
          description:
            error?.data?.message ||
            "Failed to update form dates and activate. Please try again.",
        });
      }
    } finally {
      setIsStarting(false);
      setShowActivateDateModal(false);
      setSelectedFormForActivation(null);
    }
  };

  const handleEdit = (record) => {
    // Ensure organization data is included when editing from organization details
    const formWithOrg = {
      ...record,
      organization: {
        _id: organizationId,
        name: organizationData?.data?.name || "Current Organisation ",
      },
    };
    setEditingForm(formWithOrg);
    setEditModalVisible(true); // Use editModalVisible instead of createFormModalVisible
  };
  const handleDeleteCampaign = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this campaign?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteCampaign(record._id).unwrap();
          notification.success({
            message: "Success",
            description: "Campaign deleted successfully",
          });
          refetchOrganization();
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.message || "Failed to delete campaign",
          });
        }
      },
    });
  };

  const getCampaignActionsMenuItems = () => [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    },
  ];

  const getFormActionsMenuItems = (record) => {
    const items = [
      { key: "edit", label: "Edit", icon: <EditOutlined /> },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <DeleteOutlined />,
      },
    ];

    if (record.status === "active") {
      items.push({ key: "end", label: "End", icon: <StopOutlined /> });
    } else if (record.status === "draft" || record.status === "not_active") {
      items.push({ key: "start", label: "Start", icon: <PlayCircleOutlined /> });
    }

    return items;
  };

  const handleCampaignActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEditCampaign(record);
        break;
      case "delete":
        handleDeleteCampaign(record);
        break;
      default:
        break;
    }
  };

  const handleFormActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEdit(record);
        break;
      case "delete":
        handleDelete(record);
        break;
      case "end":
        handleStatusChange(record, "not_active");
        break;
      case "start":
        handleStatusChange(record, "active");
        break;
      default:
        break;
    }
  };

  const renderActionsDropdown = (record, type) => (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        {...ACTIONS_DROPDOWN_PROPS}
        menu={{
          items:
            type === "campaigns"
              ? getCampaignActionsMenuItems()
              : getFormActionsMenuItems(record),
          onClick: (event) =>
            type === "campaigns"
              ? handleCampaignActionMenuClick(record, event.key, event)
              : handleFormActionMenuClick(record, event.key, event),
        }}
      >
        <button
          type="button"
          className="actions-dots-btn"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open actions for ${record?.title || type.slice(0, -1)}`}
        >
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );

  const getColumns = (type) => {    if (type === "campaigns") {
      return [
        {
          title: "Sr#",
          width: 70,
          render: (_, __, index) => index + 1,
        },
        {
          title: "Campaign",
          key: "title",
          render: (_, record) => (
            <div>
              <div className="font-medium text-gray-900">{record.title}</div>
              <div className="text-sm text-gray-500 truncate">
                {record.description}
              </div>
            </div>
          ),
        },
        {
          title: "Forms",
          key: "forms",
          render: (record) => <span>{record.forms?.length || 0}</span>,
        },
        {
          title: "Status",
          key: "status",
          render: (_, record) => (
            <span
              className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(
                record.status
              )}`}
            >
              {getStatusDisplayText(record.status)}
            </span>
          ),
        },
        {
          title: "",
          key: "actions",
          width: 80,
          align: "center",
          render: (_, record) => renderActionsDropdown(record, "campaigns"),
        },
      ];
    }

    // Forms columns
    return [      {
        title: "Sr#",
        width: 70,
        render: (_, __, index) => index + 1,
      },
      {
        title: "Form",
        key: "form",
        render: (_, record) => (
          <div>
            <div className="font-medium text-gray-900">{record.title}</div>
            <div className="text-sm text-gray-500 truncate max-w-xl">
              {record.description}
            </div>
            <span className="inline-block px-3 py-1 border text-sm rounded-full mt-1">
              {record.type}
            </span>
          </div>
        ),
      },
      {
        title: "Questions",
        render: (record) => (
          <span>Questions {record.questions?.length || 0}</span>
        ),
      },
      {
        title: "Responses",
        render: (record) => (
          <span>
            {record.validResponsesCount || 0} (
            {`${record.responseRatio}%` || "0%"})
          </span>
        ),
      },
      {
        title: "Status",
        render: (_, record) => (
          <span
            className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(
              record.status
            )}`}
          >
            {getStatusDisplayText(record.status)}
          </span>
        ),
      },

      {
        title: "",
        key: "actions",
        width: 80,
        align: "center",
        render: (_, record) => renderActionsDropdown(record, "forms"),
      },
    ];
  };
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const currentData =
    activeTab === "campaigns"
      ? filterData(
          withDerivedCampaignStatuses(organizationData?.data?.campaigns),
        )
      : filterData(organizationData?.data?.forms);
  const handleRowClick = (record) => {
    if (activeTab === "campaigns") {
      // Navigate to campaign details page - fix the path structure
      navigate(
        `/${window.location.pathname.split("/")[1]}/campaigns/${record._id}`,
        {
          state: record, // Pass campaign data in navigation state
        }
      );
    } else {
      // Navigate to form details page - fix the path structure
      navigate(
        `/${window.location.pathname.split("/")[1]}/forms/${record._id}`,
        {
          state: record, // Pass form data in navigation state
        }
      );
    }
  };


  const FormCard = ({ data }) => (
    <div
      className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer border"
      onClick={() => handleRowClick(data)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-2 mt-0">{data?.title}</h3>
          <p className="text-sm text-gray-500 mb-2 line-clamp-2">
            {data?.description}
          </p>
          <span className="inline-flex px-3 py-1 rounded-full text-xs border text-black">
            {data?.type}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-gray-600">
          <div>Questions: {data?.questions?.length || 0}</div>
        </div>
        <div className="text-sm text-gray-600">
          <div>Responses: {data?.responses?.length || 0}</div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span
          className={`inline-flex px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(
            data.status
          )}`}
        >
          {getStatusDisplayText(data.status)}
        </span>

        {renderActionsDropdown(data, "forms")}
      </div>    </div>
  );

  const CampaignCard = ({ data }) => (
    <div
      className="bg-white  rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
      onClick={() => handleRowClick(data)}
    >
      <div className="flex justify-between items-start mb-4 flex-wrap">
        <div>
          <h3 className="font-medium text-gray-900 truncate">{data.title}</h3>
          <p className="text-sm text-gray-500 line-clamp-2">
            {data.description}
          </p>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(
            data.status
          )}`}
        >
          {getStatusDisplayText(data.status)}
        </span>
      </div>

      <div className="flex justify-end items-center">
        {renderActionsDropdown(data, "campaigns")}
      </div>    </div>
  );

  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {currentData?.map((item) =>
        activeTab === "campaigns" ? (
          <CampaignCard key={item._id} data={item} />
        ) : (
          <FormCard key={item._id} data={item} />
        )
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stats Section */}
      <div className="bg-white  rounded-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Overview</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            value={organizationData?.data?.campaigns?.length || 0}
            label="Total Campaigns"
          />
          <StatCard
            value={
              withDerivedCampaignStatuses(
                organizationData?.data?.campaigns,
              ).filter((c) => c.status === "active").length
            }
            label="Active Campaigns"
          />
          <StatCard
            value={organizationData?.data?.forms?.length || 0}
            label="Total Forms"
          />
          <StatCard
            value={
              organizationData?.data?.forms?.filter(
                (f) => f.status === "active"
              ).length || 0
            }
            label="Active Forms"
          />
          <StatCard value="0" label="Total Submissions" />
        </div>
      </div>

      {/* Main Content Section */}
      <div className="bg-white  rounded-xl">
        {/* Search and Filters */}
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <div className="flex bg-secondary rounded-xl p-1.5 ">
            <button
              className={`view-toggle-btn ${
                viewType === "list" ? "bg-gray-900 text-white" : ""
              }`}
              onClick={() => setViewType("list")}
            >
              <List size={20} />
            </button>
            <button
              className={`view-toggle-btn ${
                viewType === "grid" ? "bg-gray-900 text-white" : ""
              }`}
              onClick={() => setViewType("grid")}
            >
              <LayoutGrid size={20} />
            </button>
          </div>
          {/* </div> */}
          <div className="relative flex-1">
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined className="text-gray-400" />}
              suffix={
                searchText && (
                  <CloseOutlined
                    className="text-gray-400 cursor-pointer"
                    onClick={() => setSearchText("")}
                  />
                )
              }
              className="h-11 rounded-xl"
            />
          </div>
          <Select
            value={sort}
            onChange={setSortBy}
            className="w-full md:w-40 h-11"
            options={[
              {
                value: "newest",
                label: "Newest",
              },
              {
                value: "oldest",
                label: "Oldest",
              },
            ]}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-[78%] md:w-40 h-11"
            options={[
              { value: "", label: "All Status" },
              {
                value: "active",
                label: "Active",
              },
              {
                value: "complete",
                label: "Completed",
              },
              {
                value: "draft",
                label: "Draft",
              },
              {
                value: "not_active",
                label: "Inactive",
              },
            ]}
          />
          <button className="h-11 w-11 flex items-center justify-center rounded-xl bg-secondary border-0">
            <LuFilter size={20} className="text-red-500" />
          </button>
        </div>

        {/* Tabs and Actions */}
        <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
          <div className="flex bg-secondary rounded-2xl p-1">
            <Button
              size="small"
              onClick={() => setActiveTab("campaigns")}
              className={`px-4 h-9 rounded-2xl ${
                activeTab === "campaigns"
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 bg-secondary border-0"
              }`}
            >
              Campaigns
            </Button>
            <Button
              size="small"
              onClick={() => setActiveTab("forms")}
              className={`px-4 h-9 rounded-2xl ${
                activeTab === "forms"
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 bg-secondary border-0"
              }`}
            >
              Forms
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <Button
              className="px-4 py-2 btnSecondary h-9"
              onClick={() => {
                setEditingCampaign(null);
                setIsModalVisible(true);
              }}
            >
              + Add Campaign
            </Button>
            <Button
              className="px-4 py-2 btnSecondary h-9"
              onClick={() => {
                setEditingForm(null);
                setEditModalVisible(true);
              }}
            >
              + Add Form
            </Button>
          </div>
        </div>

        {/* Data Table */}

        {viewType === "list" ? (
          <Table
            columns={getColumns(activeTab)}
            dataSource={currentData}
            pagination={false}
            rowKey="_id"
            className="org-details-table"
            scroll={{ x: true }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            rowClassName="cursor-pointer"
          />
        ) : (
          <GridView />
        )}
      </div>

      {/* Modals */}

      <CreateFormModal
        visible={editModalVisible}
        onClose={() => {
          setEditModalVisible(false);
          setEditingForm(null);
        }}
        organizationId={organizationId}
        editData={editingForm}
        onSuccess={() => {
          setEditModalVisible(false);
          setEditingForm(null);
          refetchOrganization();
        }}
      />
      <CreateCampaignModal
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
          setEditingCampaign(null);
        }}
        organizationId={organizationId}
        editData={editingCampaign}
        onSuccess={handleSuccess}
      />
      <ActivateDateWarningModal
        visible={showActivateDateModal}
        onCancel={() => {
          setShowActivateDateModal(false);
          setSelectedFormForActivation(null);
        }}
        onSave={handleConfirmActivationWithDates}
        initialStartDate={
          selectedFormForActivation?.start_date
            ? new Date(parseInt(selectedFormForActivation.start_date) * 1000)
                .toISOString()
                .split("T")[0]
            : new Date().toISOString().split("T")[0]
        }
        initialEndDate={
          selectedFormForActivation?.end_date
            ? new Date(parseInt(selectedFormForActivation.end_date) * 1000)
                .toISOString()
                .split("T")[0]
            : new Date().toISOString().split("T")[0]
        }
      />
    </div>
  );
};

export default OrganizationDetails;
